import React, { useState, useEffect } from 'react';
import { ChevronLeft, Pause, Play, ChevronRight } from 'lucide-react';
import AlbumItem from '../components/AlbumItem';
import FacebookVideo from '../components/FacebookVideo';
import JsContact from './JsContact';

const AlbumCollage = ({ albumTitle, albumNotes, albumType, homePath, albumCode, albumItems, selectedItem }) => {
    const [centerItem, setCenterItem] = useState(0);
    const [nextItem, setNextItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [transitioning, setTransitioning] = useState(false);
    const [pausing, setPausing] = useState(false);
    const [playVisible, setPlayVisible] = useState(false);
    const [positions, setPositions] = useState([]);

    const PAUSING_INTERVAL = 4000;
    const TRANSITIONING_INTERVAL = 3000;

    // const PAUSING_INTERVAL = 500;
    // const TRANSITIONING_INTERVAL = 1000;

    useEffect(() => {
        if (selectedItem >= 0) {
            setCenterItem(selectedItem);
        } 
    }, [selectedItem])

    useEffect(() => {
        if (albumItems.length === 0) return;

        const interval = setInterval(() => {
            if (!pausing) {
                transitionToNext(1, true);
            }
        }, PAUSING_INTERVAL);

        return () => clearInterval(interval);
    }, [albumItems.length, centerItem, pausing]);

    useEffect(() => {
        if (albumItems.length === 0) return;

        setLoading(true);
        generatePositions();
        setCenterItem(0);
        setNextItem(null);
        setTransitioning(false);
        setLoading(false);
    }, [albumItems]);

    const transitionToNext = (increment, canTransition) => {
        if (albumItems.length === 0) return;

        let next = (centerItem + increment) % albumItems.length;
        if (next < 0) {
            next = albumItems.length - 1;
        }

        setNextItem(next);
        setTransitioning(canTransition);

        setTimeout(() => {
            setCenterItem(next);
            setNextItem(null);
            setTransitioning(false);
        }, canTransition ? TRANSITIONING_INTERVAL : 0);
    };

    const generatePositions = () => {
        const newPositions = albumItems.map((_, index) => {
            const side = Math.floor(index / Math.floor(albumItems.length / 7));
            let top, left;
            const leftOffset = 10.5;

            switch (side) {
                case 0:
                    top = Math.floor(Math.floor(Math.random() * 5)) + 72;
                    left = Math.floor(Math.random() * 10);
                    break;
                case 1:
                    top = Math.floor(Math.random() * 5) + 72;
                    left = Math.floor(Math.random() * 10) + 1 * leftOffset;
                    break;
                case 2:
                    top = Math.floor(Math.random() * 5) + 72;
                    left = Math.floor(Math.random() * 10) + 2 * leftOffset;
                    break;
                    
                case 3:
                    top = Math.floor(Math.random() * 5) + 72;
                    left = Math.floor(Math.random() * 10) + 3 * leftOffset;
                    break;
                case 4:
                    top = Math.floor(Math.random() * 5) + 72;
                    left = Math.floor(Math.random() * 10) + 4 * leftOffset;
                    break;

                case 5:
                    top = Math.floor(Math.random() * 5) + 72;
                    left = Math.floor(Math.random() * 10) + 5 * leftOffset;
                    break;
                case 6:
                    top = Math.floor(Math.random() * 5) + 72;
                    left = Math.floor(Math.random() * 10) + 6 * leftOffset;
                    break;
                case 7:
                    top = Math.floor(Math.random() * 5) + 72;
                    left = Math.floor(Math.random() * 10) + 7 * leftOffset;
                    break;
            }

            return {
                top: `${top}%`,
                left: `${left}%`,
                rotation: Math.random() * 30 - 15, // -15 to +15 degrees
                scale: 0.3 + Math.random() * 0.6
            };
        });

        setPositions(newPositions);
    };

    const prevPhoto = () => {
        transitionToNext(-1, false);
    };

    const nextPhoto = () => {
        transitionToNext(1, false);
    };

    const togglePausing = () => {
        if (pausing) {
            transitionToNext(1, false);
        }
        setPausing(!pausing);
    }

    if (albumItems.length === 0 || loading) {
        return (
            <div className="text-lg font-bold italic mt-4 text-center text-orange-200">
                Loading albums ...
            </div>
        );
    }

    return (
        <div>
            <div className='relative left-4 max-w-[calc(92vw)]'>
                <div className='relative text-orange-300'>
                    <p className="relative bottom-0 flex items-baseline text-orange-400 text-xl/tight">
                        {'JS Home Design & Remodeling Inc'}
                        <div className='hidden custom-flex ml-4'>
                            <JsContact />
                        </div>
                    </p>
                </div>
                <div className='flex custom-hidden'>
                    <JsContact />
                </div>
                <div>
                    <p className="text-orange-300 text-lg/tight">
                        {albumTitle}
                    </p>
                    <p className="text-orange-300 text-sm/tight">
                        {albumNotes}
                    </p>
                </div>
            </div>
            <div>
                {albumType === 2
                ?
                    <div className="flex justify-center min-h-[calc(64vh)] custom-min-h-80">
                        <FacebookVideo
                            videoId={albumCode}
                        />
                    </div>
                :
                    <div>
                        {/* <div className="relative min-w-[calc(100vw)] min-h-[calc(50vh)] custom-max-w-50 custom-min-h-75"> */}
                        <div className="relative custom-max-w-50 custom-min-h-75">
                            <div className='hidden custom-flex'>
                                {albumItems.map((item, index) => {
                                    if (index === centerItem) return null;

                                    const pos = positions[index];
                                    if (!pos) return null;

                                    return (
                                        <div
                                            key={item.id}
                                            className={`absolute transition-all duration-1000 cursor-pointer hover:scale-110 hover:z-30 opacity-90
                                                ${index === nextItem
                                                    ? 'animate-bounce z-40'
                                                    : ''
                                                }`}
                                            style={{
                                                top: pos.top,
                                                left: pos.left,
                                                transform: `rotate(${pos.rotation}deg) scale(${pos.scale})`,
                                            }}
                                            onClick={() => setCenterItem(index)}
                                        >
                                            <AlbumItem
                                                homePath={homePath}
                                                albumCode={albumCode}
                                                fileName={albumItems[index].filename}
                                                size={'max-w-32 max-h-32 border-1'}
                                            />                  
                                        </div>
                                    );
                                })}
                            </div>
                            
                            <div className='relative'>
                                <div className="flex items-center justify-center w-full h-full transform">
                                    <div
                                        className={`transition-opacity duration-1000 ease-in-out ${
                                            transitioning ? 'opacity-100' : 'opacity-100'
                                        }`}
                                        // onClick={() => togglePausing()}
                                    >
                                        <div className='absolute top-[calc(16vh)] custom-top-24 left-4'>
                                            <button
                                                onClick={prevPhoto}
                                                className="flex w-10 h-10 mx-1 bg-white/10 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 hover:bg-white/20 transition-all duration-300 border border-white/20 hover:scale-110"
                                            >
                                                <ChevronLeft size={24} />
                                            </button>
                                        </div>
                                        <div>
                                            <AlbumItem
                                                homePath={homePath}
                                                albumCode={albumCode}
                                                fileName={albumItems[centerItem].filename}
                                                // size={'max-w-96 max-h-96 md:max-w-228 md:min-h-[calc(55vh)]'}
                                                size={'max-w-96 max-h-96 custom-max-w-50 custom-max-h-50'}
                                            />                  
                                        </div>
                                        <div className='absolute top-[calc(16vh)] custom-top-24 right-4'>
                                            <button
                                                onClick={nextPhoto}
                                                className="flex w-10 h-10 mx-1 bg-white/10 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 hover:bg-white/20 transition-all duration-300 border border-white/20 hover:scale-110"
                                            >
                                                <ChevronRight size={24} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className='absolute bg-transparent top-[calc(16vh)] custom-top-24 bottom-20 left-20 right-20'
                                    onMouseEnter={() => {
                                        setPlayVisible(true);
                                    }}
                                    onMouseLeave={() => {
                                        setPlayVisible(false);
                                    }}
                                    onTouchStart={() => {
                                        setPlayVisible(true);
                                    }}
                                    onTouchEnd={() => {
                                        setPlayVisible(false);
                                    }}
                                >
                                    {playVisible &&
                                        <div className='flex justify-center'>
                                            <button
                                                onClick={togglePausing}
                                                className={`flex w-10 h-10 mx-1 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 hover:bg-white/20 transition-all duration-300 border border-white/20 hover:scale-110 ${
                                                    !pausing 
                                                    ? 'bg-white/10 hover:bg-white/20' 
                                                    : 'bg-white/10 hover:bg-white/20'
                                                }`}
                                            >
                                                {pausing 
                                                    ? <Play size={24} />
                                                    : <Pause size={24} />
                                                }
                                            </button>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </div>
        </div>
    );
};

export default AlbumCollage;

